
import { useState, useEffect } from 'react';
import { useCrypto } from '../../hooks/useCrypto';
import Header from '../../components/feature/Header';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import Card from '../../components/base/Card';

interface OrderBook {
  price: number;
  amount: number;
  total: number;
}

const Trading = () => {
  const { cryptoData, loading, buyCrypto, sellCrypto, walletBalances } = useCrypto();
  const [selectedCrypto, setSelectedCrypto] = useState('bitcoin');
  const [orderType, setOrderType] = useState<'buy' | 'sell'>('buy');
  const [tradeType, setTradeType] = useState<'market' | 'limit'>('market');
  const [amount, setAmount] = useState('');
  const [price, setPrice] = useState('');
  const [balance] = useState(50000); // Mock USD balance
  const [isTrading, setIsTrading] = useState(false);

  const selectedCoin = cryptoData.find(coin => coin.id === selectedCrypto);
  const selectedBalance = walletBalances.find(b => b.asset === selectedCrypto);

  // Update price when crypto changes
  useEffect(() => {
    if (selectedCoin && tradeType === 'limit') {
      setPrice(selectedCoin.current_price.toString());
    }
  }, [selectedCoin, tradeType]);

  // Mock order book data
  const [orderBook] = useState<{buy: OrderBook[], sell: OrderBook[]}>({
    buy: [
      { price: 43250, amount: 0.5, total: 21625 },
      { price: 43240, amount: 1.2, total: 51888 },
      { price: 43230, amount: 0.8, total: 34584 },
      { price: 43220, amount: 2.1, total: 90762 },
      { price: 43210, amount: 0.3, total: 12963 },
    ],
    sell: [
      { price: 43260, amount: 0.7, total: 30282 },
      { price: 43270, amount: 1.5, total: 64905 },
      { price: 43280, amount: 0.9, total: 38952 },
      { price: 43290, amount: 1.8, total: 77922 },
      { price: 43300, amount: 0.4, total: 17320 },
    ]
  });

  const handleTrade = async () => {
    if (!amount || (tradeType === 'limit' && !price)) {
      alert('Please fill in all required fields');
      return;
    }
    
    const tradeAmount = parseFloat(amount);
    const tradePrice = tradeType === 'limit' ? parseFloat(price) : selectedCoin?.current_price || 0;
    const total = tradeAmount * tradePrice;
    
    if (orderType === 'buy' && total > balance) {
      alert('Insufficient USD balance');
      return;
    }

    if (orderType === 'sell' && (!selectedBalance || selectedBalance.balance < tradeAmount)) {
      alert(`Insufficient ${selectedCoin?.symbol.toUpperCase()} balance`);
      return;
    }
    
    setIsTrading(true);
    
    try {
      let success = false;
      
      if (orderType === 'buy') {
        success = buyCrypto(selectedCoin?.symbol || '', tradeAmount, tradePrice);
      } else {
        success = sellCrypto(selectedCoin?.symbol || '', tradeAmount, tradePrice);
      }
      
      if (success) {
        alert(`${orderType.toUpperCase()} order executed successfully!\n${tradeAmount} ${selectedCoin?.symbol.toUpperCase()} at $${tradePrice.toLocaleString()}`);
        setAmount('');
        setPrice(tradeType === 'limit' ? selectedCoin?.current_price.toString() || '' : '');
      } else {
        alert('Trade failed. Please check your balance and try again.');
      }
    } catch (error) {
      alert('Trade failed. Please try again.');
    } finally {
      setIsTrading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading trading data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Trading</h1>
          <p className="text-gray-600">Buy and sell cryptocurrencies with real-time market data</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Market Selection */}
          <div className="lg:col-span-1">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Markets</h3>
              <div className="space-y-2">
                {cryptoData.slice(0, 10).map((coin) => (
                  <div
                    key={coin.id}
                    onClick={() => setSelectedCrypto(coin.id)}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedCrypto === coin.id
                        ? 'bg-blue-100 border-2 border-blue-500'
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <img src={coin.image} alt={coin.name} className="w-6 h-6" />
                        <div>
                          <p className="font-medium text-sm">{coin.symbol.toUpperCase()}</p>
                          <p className="text-xs text-gray-500">{coin.name}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">${coin.current_price.toLocaleString()}</p>
                        <p className={`text-xs ${
                          coin.price_change_percentage_24h >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {coin.price_change_percentage_24h >= 0 ? '+' : ''}
                          {coin.price_change_percentage_24h.toFixed(2)}%
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Trading Interface */}
          <div className="lg:col-span-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Order Book */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Order Book</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-red-600 mb-2">Sell Orders</h4>
                    <div className="space-y-1">
                      {orderBook.sell.reverse().map((order, index) => (
                        <div key={index} className="flex justify-between text-xs">
                          <span className="text-red-600">${order.price.toLocaleString()}</span>
                          <span>{order.amount}</span>
                          <span className="text-gray-500">${order.total.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="border-t pt-2">
                    <div className="text-center py-2">
                      <span className="text-lg font-bold">
                        ${selectedCoin?.current_price.toLocaleString()}
                      </span>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-green-600 mb-2">Buy Orders</h4>
                    <div className="space-y-1">
                      {orderBook.buy.map((order, index) => (
                        <div key={index} className="flex justify-between text-xs">
                          <span className="text-green-600">${order.price.toLocaleString()}</span>
                          <span>{order.amount}</span>
                          <span className="text-gray-500">${order.total.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>

              {/* Price Chart Placeholder */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Price Chart</h3>
                <div className="h-64 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <i className="ri-line-chart-line text-4xl text-blue-500 mb-2"></i>
                    <p className="text-gray-600">Interactive chart coming soon</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Recent Trades */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Recent Trades</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">Time</th>
                      <th className="text-left py-2">Price</th>
                      <th className="text-left py-2">Amount</th>
                      <th className="text-left py-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { time: '14:32:15', price: 43255, amount: 0.5, side: 'buy' },
                      { time: '14:31:42', price: 43248, amount: 1.2, side: 'sell' },
                      { time: '14:31:18', price: 43260, amount: 0.8, side: 'buy' },
                      { time: '14:30:55', price: 43245, amount: 2.1, side: 'sell' },
                      { time: '14:30:33', price: 43252, amount: 0.3, side: 'buy' },
                    ].map((trade, index) => (
                      <tr key={index} className="border-b">
                        <td className="py-2 text-gray-600">{trade.time}</td>
                        <td className={`py-2 ${trade.side === 'buy' ? 'text-green-600' : 'text-red-600'}`}>
                          ${trade.price.toLocaleString()}
                        </td>
                        <td className="py-2">{trade.amount}</td>
                        <td className="py-2">${(trade.price * trade.amount).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          {/* Trading Panel */}
          <div className="lg:col-span-1">
            <Card className="p-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">Place Order</h3>
                
                {/* Order Type Tabs */}
                <div className="flex bg-gray-100 rounded-lg p-1 mb-4">
                  <button
                    onClick={() => setOrderType('buy')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                      orderType === 'buy'
                        ? 'bg-green-500 text-white'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Buy
                  </button>
                  <button
                    onClick={() => setOrderType('sell')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                      orderType === 'sell'
                        ? 'bg-red-500 text-white'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Sell
                  </button>
                </div>

                {/* Trade Type Tabs */}
                <div className="flex bg-gray-100 rounded-lg p-1 mb-4">
                  <button
                    onClick={() => setTradeType('market')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                      tradeType === 'market'
                        ? 'bg-blue-500 text-white'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Market
                  </button>
                  <button
                    onClick={() => setTradeType('limit')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                      tradeType === 'limit'
                        ? 'bg-blue-500 text-white'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Limit
                  </button>
                </div>

                {/* Order Form */}
                <div className="space-y-4">
                  {tradeType === 'limit' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Price (USD)
                      </label>
                      <Input
                        type="number"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        placeholder="0.00"
                        className="text-sm"
                      />
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Amount ({selectedCoin?.symbol.toUpperCase()})
                    </label>
                    <Input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      className="text-sm"
                    />
                  </div>

                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">USD Balance:</span>
                      <span className="font-medium">${balance.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">{selectedCoin?.symbol.toUpperCase()} Balance:</span>
                      <span className="font-medium">{selectedBalance?.balance.toFixed(6) || '0.000000'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Estimated Total:</span>
                      <span className="font-medium">
                        ${amount && selectedCoin ? 
                          (parseFloat(amount) * (tradeType === 'limit' ? parseFloat(price || '0') : selectedCoin.current_price)).toLocaleString() 
                          : '0.00'
                        }
                      </span>
                    </div>
                  </div>

                  <Button
                    onClick={handleTrade}
                    disabled={isTrading}
                    className={`w-full whitespace-nowrap ${
                      orderType === 'buy' 
                        ? 'bg-green-500 hover:bg-green-600' 
                        : 'bg-red-500 hover:bg-red-600'
                    } text-white disabled:bg-gray-400 disabled:cursor-not-allowed`}
                  >
                    {isTrading ? (
                      <div className="flex items-center justify-center">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Processing...
                      </div>
                    ) : (
                      `${orderType === 'buy' ? 'Buy' : 'Sell'} ${selectedCoin?.symbol.toUpperCase()}`
                    )}
                  </Button>
                </div>
              </div>

              {/* Quick Amount Buttons */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Quick Amount</h4>
                <div className="grid grid-cols-2 gap-2">
                  {['25%', '50%', '75%', '100%'].map((percent) => (
                    <button
                      key={percent}
                      onClick={() => {
                        if (orderType === 'sell' && selectedBalance) {
                          const percentage = parseInt(percent) / 100;
                          setAmount((selectedBalance.balance * percentage).toFixed(6));
                        } else if (orderType === 'buy' && selectedCoin) {
                          const percentage = parseInt(percent) / 100;
                          const tradePrice = tradeType === 'limit' ? parseFloat(price || '0') : selectedCoin.current_price;
                          if (tradePrice > 0) {
                            const maxAmount = (balance * percentage) / tradePrice;
                            setAmount(maxAmount.toFixed(6));
                          }
                        }
                      }}
                      className="py-2 px-3 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {percent}
                    </button>
                  ))}
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Trading;